/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Project } from '@ips-jit/schema';
import { Box, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';

import { AgentStatus } from './AgentStatus';
import { ChevronIcon, DotIcon } from './icons';
import { PLATFORM_LABEL } from './platform';

const Fact = ({ label, value }: { label: string; value: string | number }) => (
  <Stack component="li" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
    <DotIcon sx={{ fontSize: 10, color: 'primary.main' }} />
    <Typography variant="body2">
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {label}:
      </Box>{' '}
      {value}
    </Typography>
  </Stack>
);

export interface SettingsPanelProps {
  project: Project;
  devices: Device[];
  deviceId: string;
  onDeviceChange: (deviceId: string) => void;
  open: boolean;
  onToggle: () => void;
  /** The App section, below the runtime. */
  children?: React.ReactNode;
}

/**
 * The project: what it targets, the runtime (the PC, its agent, app and cameras) it is built for, and the app's builds.
 * Folds to a thin strip.
 */
export const SettingsPanel = ({
  project,
  devices,
  deviceId,
  onDeviceChange,
  open,
  onToggle,
  children,
}: SettingsPanelProps) => {
  const chosen = devices.find((device) => device.device_id === deviceId);
  if (!open) {
    return (
      <Paper
        variant="outlined"
        component="section"
        aria-label="Project"
        sx={{ height: '100%', py: 1, textAlign: 'center' }}
      >
        <IconButton size="small" aria-label="Show the project panel" onClick={onToggle}>
          <ChevronIcon direction="right" fontSize="small" />
        </IconButton>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: 'block', writingMode: 'vertical-rl', mx: 'auto', mt: 1 }}
        >
          Project
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" component="section" aria-label="Project" sx={{ p: 2, overflowY: 'auto', height: '100%' }}>
      <Stack spacing={3}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">PROJECT</Typography>
          <IconButton size="small" aria-label="Hide the project panel" onClick={onToggle}>
            <ChevronIcon direction="left" fontSize="small" />
          </IconButton>
        </Stack>
        <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
          <Fact label="Name" value={project.name} />
          <Fact label="Platform" value={PLATFORM_LABEL[project.platform]} />
          <Fact label="Cameras" value={project.camera_models.join(', ') || '—'} />
          <Fact label="Version" value={project.current_version} />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            Runtime
          </Typography>
          {project.runtime_id !== null ? (
            // A project is made for one runtime and stays with it.
            <Typography variant="body2" aria-label="Runtime">
              {chosen ? `${chosen.name} (${chosen.status})` : 'The runtime of this project was deleted.'}
            </Typography>
          ) : (
            <TextField
              select
              label="Runtime"
              size="small"
              value={deviceId}
              onChange={(event) => onDeviceChange(event.target.value)}
              // The browser's own list: it is not a popup over the page, so nothing the page does can close it.
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              helperText={
                devices.length === 0
                  ? 'Add a runtime on the Runtimes page first; the app is built for one runtime.'
                  : 'The app is built for this runtime and sent to it when you approve a plan.'
              }
            >
              {devices.length === 0 && <option value="">No device</option>}
              {devices.map((device) => (
                <option key={device.device_id} value={device.device_id}>
                  {device.name} ({device.status})
                </option>
              ))}
            </TextField>
          )}
          {chosen && <AgentStatus device={chosen} />}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
};
